import React, {useEffect, useState} from "react";
import {formatNumberWithSeparator} from "../../../Hooks/UseGenericFunctions";
import {VATIcon} from "../../../Components/Cars/VATIcon";
import {PurchaseTypePill} from "../../../Components/Information/PurchaseTypePill";
import {CarAttribute} from "../../Showroom/ShowroomCarCard/CarAttribute";
import {ChevronRightIcon} from "@heroicons/react/24/solid";
import {EquipmentIcons} from "../../../Components/Equipment/EquipmentIcons";
import {useUrlParams} from "../../../Hooks/useUrlParams";
import {LikeCar} from "../../Presales/LikeCar";
import {CarDetailsMobile} from "../../../Components/Cars/CarDetailsMobile";
import {ChevronLeftIcon} from "@heroicons/react/24/outline";
import {AddToBasketButton, SizeEnum} from "../../../Components/ShoppingBasket/AddToBasketButton";

const BasketStatusPill = ({car}) => {
    const {setParam} = useUrlParams();
    const handleClick = (e) => {
        e.stopPropagation();
        setParam("showBasket", "true");
    };

    let result = "";
    if (car.offer_amount_unconfirmed > 0 && car.offer_amount_confirmed === null) result = <div onClick={handleClick} className="underline font-bold whitespace-nowrap">Confirm your offer of {formatNumberWithSeparator(parseInt(car.offer_amount_unconfirmed), '.')} {car.currency}</div>
    if (car.offer_amount_unconfirmed === null && car.offer_amount_confirmed > 0) result = <div>Your offer is {formatNumberWithSeparator(parseInt(car.offer_amount_confirmed), '.')} {car.currency}</div>
    if (car.offer_amount_unconfirmed === null && car.offer_amount_confirmed === null && car.in_basket) result = "In basket";

    return (
        <div>
            <div className="rounded-2xl px-2 py-1 bg-purchase-color text-white text-xs">
                {result}
            </div>
        </div>
    )
}
export const MobileShowroomCarCard = React.memo(({car, hidePrices, equipmentBaseIcons, selectedCarId, setSelectedCarId, fnAfterUpdate, scrollIntoViewAfterRender}) => {
    const [expandedView, setExpandedView] = useState(false);

    useEffect(() => {
        if (car.hash === selectedCarId) {
            setExpandedView(true);
        }
    }, [selectedCarId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (expandedView) {
            console.log(car.hash);
            scrollIntoViewAfterRender(car.hash);
        }
    }, [expandedView]);

    const fnCardClicked = () => {
        if (expandedView) {
            setSelectedCarId(null);
            setExpandedView(false);
        } else {
            setSelectedCarId(car.hash);
            setExpandedView(true);
        }
    }

    return (
        <div className={`relative bg-white rounded-xl w-full ${expandedView ? '' : 'h-[530px] overflow-hidden'} group cursor-pointer`}>
            <div className={`relative ${!expandedView ? 'bg-gray-300' : ''}`} onClick={fnCardClicked}>
                <img className='object-cover rounded-t-xl w-full h-[300px]' loading="lazy" src={car.list_image_medium} alt={'image for ' + car.designation}/>
            </div>

            <div className={`p-6 absolute ${expandedView ? 'top-[250px]' : 'bottom-0'} left-0 bg-white w-full transition-all duration-500 ease-in-out`}>
                {car.equipment_base_icons !== null &&
                    <div className="absolute left-2 border border-gray-200 top-[-40px] rounded-xl bg-white px-4 py-1">
                        <EquipmentIcons carId={car.id} equipment={car.equipment_base_icons} equipmentBaseIcons={equipmentBaseIcons} size="24px" opacity={0.3}/>
                    </div>
                }

                <div className="w-full flex items-center justify-between">
                    <div className="text-lg truncate overflow-hidden whitespace-nowrap">
                        {car.designation}
                    </div>
                    <div>
                        <LikeCar car={car} fnAfterUpdate={fnAfterUpdate} iconSize="w-8 h-8"/>
                    </div>
                </div>
                {!hidePrices &&
                    <div className="font-bold text-2xl flex items-center">
                        {formatNumberWithSeparator(parseInt(car.price), '.')}&nbsp;{car.currency}&nbsp;<VATIcon vatStatusId={car.vat_status_id}/>
                    </div>
                }
                <div className="mt-1 mb-6 text-gray-100 flex items-center" onClick={fnCardClicked}>
                    <div className="mr-2"><PurchaseTypePill car={car} className="rounded-2xl px-2"/></div>
                    {(car.offer_amount_unconfirmed > 0 || car.offer_amount_confirmed > 0 || car.in_basket) &&
                        <div><BasketStatusPill car={car}/></div>
                    }
                </div>
                <div className="grid grid-cols-2 gap-8" onClick={fnCardClicked}>
                    <div className="border-r-1">
                        <CarAttribute label="Car #" value={car.car_id}/>
                        <CarAttribute label="First reg." value={car.reg_date_show}/>
                        <CarAttribute label="KM" value={formatNumberWithSeparator(parseInt(car.km), '.')}/>
                        <CarAttribute label="Damages" value={formatNumberWithSeparator(parseInt(car.damage_amount), '.') + ' €'}/>
                    </div>
                    <div>
                        <CarAttribute label="Color" value={car.color}/>
                        <CarAttribute label="HP" value={car.horsepower}/>
                        <CarAttribute label="Fuel" value={car.fuel_name}/>
                        {car.battery_size && <CarAttribute label="Battery Size" value={car.battery_size}/>}
                    </div>
                </div>
                {!expandedView &&
                    <div className="max-h-0 opacity-0 max-h-20 opacity-100 transition-all duration-700 ease-in-out border-t mt-4" onClick={fnCardClicked}>
                        <div className="flex items-center justify-between mt-4">
                            <div>View more</div>
                            <div><ChevronRightIcon className="w-6 h-6 text-gray-600" strokeWidth={1.6}/></div>
                        </div>
                    </div>
                }
            </div>

            <div>
                {expandedView &&
                    <div className="mt-[200px]">
                        <div className="flex justify-center">
                            <AddToBasketButton car={car} fnUpdate={fnAfterUpdate} carInBasket={car.in_basket} className="w-[100px]" text="Add to basket" size={SizeEnum.Big}/>
                        </div>
                        <CarDetailsMobile car={car} showPrice={true} open={selectedCarId === car.hash} close={() => {
                            setSelectedCarId = null;
                            setExpandedView(false)
                        }} selectedCarId={selectedCarId}/>
                        <div className="max-h-0 px-6 pb-4 opacity-0 max-h-20 opacity-100 transition-all duration-700 ease-in-out border-t mt-4">
                            <div className="flex items-center justify-between mt-4" onClick={fnCardClicked}>
                                <div>View less</div>
                                <div><ChevronLeftIcon className="w-6 h-6 text-gray-600" strokeWidth={1.6}/></div>
                            </div>
                        </div>
                    </div>
                }
            </div>
        </div>
    )
})